import React, { useEffect, useRef } from 'react';
import { LineageNodeType } from '../../../types/lineage.types';
import * as styles from './LineageContextMenu.css.ts';

export interface LineageContextMenuProps {
  /** メニューの表示状態 */
  visible: boolean;
  /** メニューのX座標 */
  x: number;
  /** メニューのY座標 */
  y: number;
  /** 対象ノードのタイプ */
  nodeType: LineageNodeType | null;
  /** メニューを閉じる */
  onClose: () => void;
  /** Open/Focusアクション */
  onOpen: () => void;
  /** Delete/Closeアクション */
  onDelete: () => void;
}

/**
 * Project Lineageノードのコンテキストメニュー
 */
export const LineageContextMenu: React.FC<LineageContextMenuProps> = ({
  visible,
  x,
  y,
  nodeType,
  onClose,
  onOpen,
  onDelete
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // 外部クリックでメニューを閉じる
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // 少し遅延させて、右クリックイベント自体で閉じないようにする
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose]);

  if (!visible || !nodeType) {
    return null;
  }

  // ノードタイプに応じたラベルを取得
  const getOpenLabel = (): string => {
    if (nodeType === 'tab') {
      return 'Focus';
    }
    return 'Open';
  };

  const getDeleteLabel = (): string => {
    if (nodeType === 'tab') {
      return 'Close';
    }
    return 'Delete';
  };

  const handleOpen = () => {
    onOpen();
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  // 画面外にはみ出さないように位置を調整
  const adjustedPosition = {
    left: Math.min(x, window.innerWidth - 200),
    top: Math.min(y, window.innerHeight - 100)
  };

  return (
    <div
      ref={menuRef}
      className={styles.contextMenu}
      style={{
        left: adjustedPosition.left,
        top: adjustedPosition.top
      }}
      data-testid="lineage-context-menu"
    >
      <button
        className={styles.contextMenuItem}
        onClick={handleOpen}
        data-testid="lineage-context-menu-open"
      >
        {getOpenLabel()}
      </button>
      <div className={styles.separator} />
      <button
        className={styles.contextMenuItemDanger}
        onClick={handleDelete}
        data-testid="lineage-context-menu-delete"
      >
        {getDeleteLabel()}
      </button>
    </div>
  );
};

export default LineageContextMenu;
